import { Dialog, Link, Typography } from '@material-ui/core';
import { Flag } from '@material-ui/icons';
import { parseDate } from '@superdispatch/dates';
import {
  CheckboxField,
  ColorDynamic,
  ExitTransitionPlaceholder,
  Image,
  Inline,
  Stack,
  useResponsiveValue,
} from '@superdispatch/ui';
import {
  AnchorButton,
  Button,
  FlagList,
  FlagListItem,
} from '@superdispatch/ui-lab';
import { useEffect, useMemo, useState } from 'react';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { trackEvent } from 'shared/helpers/Analytics';
import { ShipperExperienceDTO } from 'shared/modules/shipper/ShipperProfileDTO';
import { ResponsiveTooltip } from 'shared/ui/ResponsiveTooltip';
import styled from 'styled-components';
import limitedExperience from './assets/limited_experience.png';
import limitedExperienceDark from './assets/limited_experience_dark.png';
import { saveShippersExperienceWarningPreference } from './ShipperUtils';

const Illustration = styled(Image)`
  width: 100%;
`;

const CustomContainer = styled.div<{ isFullHeight: boolean }>`
  display: flex;
  flex-direction: column;
  height: ${({ isFullHeight }) => (isFullHeight ? '100vh' : '100%')};
`;

const CustomTitle = styled.div`
  padding: 16px;
`;

const CustomContent = styled.div`
  padding: 0 16px;
`;

const CenteredActions = styled.div`
  margin-top: auto;
  justify-content: center;
  padding: 16px 16px 24px;
`;

const CustomCheckBoxWrapper = styled.div`
  & .MuiFormControlLabel-root {
    ${({ theme }) => theme.breakpoints.down('sm')} {
      margin-left: -8px;
      margin-right: 0;
    }
  }
`;

const List = styled.ul`
  margin: 0;
  padding-left: 20px;
`;

const TooltipIcon = styled.div`
  background-color: ${ColorDynamic.White};

  padding: 4px;
  border-radius: 50%;

  & > svg {
    color: ${ColorDynamic.Dark500};
    font-size: 16px;
  }
`;

interface TooltipContentProps {
  shipperExperience: ShipperExperienceDTO;
}

export function TooltipContent({ shipperExperience }: TooltipContentProps) {
  return (
    <Stack>
      <Inline verticalAlign="center" space="xsmall">
        <TooltipIcon>
          <Flag />
        </TooltipIcon>
        <Typography variant="h3">This is a new account</Typography>
      </Inline>
      {shipperExperience.on_platform_since && (
        <Typography variant="h4">
          Joined Super Dispatch: <br />
          {parseDate(shipperExperience.on_platform_since, {
            format: 'DateISO',
          }).toFormat('LLLL yyyy')}
        </Typography>
      )}
      {(shipperExperience.delivered_vehicles_insufficient ||
        shipperExperience.received_ratings_insufficient) && (
        <Stack space="xsmall">
          <Typography variant="h4">Limited activity indicators:</Typography>
          <List>
            {shipperExperience.received_ratings_insufficient && (
              <li>
                <Typography variant="h4">Fewer than 3 ratings</Typography>
              </li>
            )}
            {shipperExperience.delivered_vehicles_insufficient && (
              <li>
                <Typography variant="h4">
                  Under 5 vehicles transported
                </Typography>
              </li>
            )}
          </List>
        </Stack>
      )}
    </Stack>
  );
}

interface FlagsProps {
  shipperExperience: ShipperExperienceDTO;
}

function Flags({ shipperExperience }: FlagsProps) {
  const isMobile = useResponsiveValue(true, false);
  const target = MobileAppBridge.isInjected() ? '_self' : '_blank';
  const url = `/loadboard/shipper/${shipperExperience.guid}?utm_source=Web+CTMS&utm_medium=Limited+Experience`;

  return (
    <FlagList
      key={shipperExperience.guid}
      title={
        <Link rel="noreferrer" target={target} href={url}>
          {shipperExperience.name}
        </Link>
      }
      endAction={
        <AnchorButton
          fullWidth={isMobile}
          variant="neutral"
          target={target}
          href={url}
        >
          View Profile
        </AnchorButton>
      }
    >
      <ResponsiveTooltip
        placement="top-end"
        title={<TooltipContent shipperExperience={shipperExperience} />}
      >
        <FlagListItem showHelpIcon={true}>
          <Typography variant="body1">New Shipper</Typography>
        </FlagListItem>
      </ResponsiveTooltip>
    </FlagList>
  );
}

interface LimitedExperienceDialogProps {
  shippersExperience: ShipperExperienceDTO[];
  isOpen: boolean;
  onCancel?: () => void;
  onConfirm: (shouldNotWarn: boolean) => void;
  action: 'accept_offer' | 'send_request';
  isFullHeight?: boolean;
}

export function LimitedExperience({
  shippersExperience,
  onCancel,
  onConfirm,
  action,
  isFullHeight = false,
}: LimitedExperienceDialogProps) {
  const isMobile = useResponsiveValue(true, false);
  const [shouldNotWarn, setShouldNotWarn] = useState(false);
  const source = action === 'accept_offer' ? 'Accept Offer' : 'Send Request';
  const limitedExperienceList = useMemo(
    () =>
      shippersExperience.filter((shipper) => shipper.has_limited_experience),
    [shippersExperience],
  );

  useEffect(() => {
    trackEvent('Carrier Opened Limited Experience Dialog', {
      utm_medium: source,
    });
  }, [source]);

  function handleConfirm() {
    trackEvent(
      'Carrier Clicked I Understand the Risks In Limited Experience Dialog',
      {
        utm_medium: source,
      },
    );

    if (shouldNotWarn) {
      saveShippersExperienceWarningPreference(
        limitedExperienceList.map((shipper) => shipper.guid),
      );
    }

    onConfirm(shouldNotWarn);
  }

  return (
    <CustomContainer isFullHeight={isFullHeight}>
      <CustomTitle>
        <Stack space="small">
          <Illustration
            src={limitedExperience}
            srcDark={limitedExperienceDark}
            alt="Limited expirience shipper warning"
          />

          <Stack space="xsmall">
            <Typography variant="h3">New Shipper Alert</Typography>

            {limitedExperienceList.length > 1 ? (
              <Typography variant="h4">
                These shippers have been verified but are new to auto hauling.
                We recommend doing additional research.
              </Typography>
            ) : (
              <Typography variant="h4">
                This shipper has been verified but is new to auto hauling. We
                recommend doing additional research.
              </Typography>
            )}
          </Stack>
        </Stack>
      </CustomTitle>
      <CustomContent>
        <Stack space="small">
          {limitedExperienceList.map((shipper) => (
            <Flags shipperExperience={shipper} key={shipper.guid} />
          ))}
        </Stack>
      </CustomContent>

      <CenteredActions>
        <Stack space="small">
          <CustomCheckBoxWrapper>
            <CheckboxField
              onChange={(_, value) => {
                setShouldNotWarn(value);
              }}
              label="Don't notify me about this shipper again"
            />
          </CustomCheckBoxWrapper>
          {isFullHeight ? (
            <Button fullWidth={true} onClick={handleConfirm}>
              Continue With Shipper
            </Button>
          ) : (
            <Inline space="small" horizontalAlign="center">
              <Button fullWidth={isMobile} onClick={handleConfirm}>
                Continue With Shipper
              </Button>
              <Button
                fullWidth={isMobile}
                variant="neutral"
                onClick={() => {
                  trackEvent(
                    'Carrier Clicked Cancel In Limited Experience Dialog',
                    {
                      utm_medium: source,
                    },
                  );
                  onCancel?.();
                }}
              >
                Cancel
              </Button>
            </Inline>
          )}
        </Stack>
      </CenteredActions>
    </CustomContainer>
  );
}

export function LimitedExperienceDialog(props: LimitedExperienceDialogProps) {
  const isMobile = useResponsiveValue(true, false);
  return (
    <Dialog
      open={props.isOpen}
      onClose={props.onCancel}
      maxWidth="sm"
      fullWidth={true}
      fullScreen={isMobile}
    >
      <ExitTransitionPlaceholder in={props.isOpen}>
        {props.isOpen && <LimitedExperience {...props} />}
      </ExitTransitionPlaceholder>
    </Dialog>
  );
}
